import {
  GridPresetOneHalf,
  GridRow,
  HideFor,
  Labels,
  OptimizelyEditProperty,
  Picture,
} from "/App.Components";
import "./TeaserItem.scss";
import { TagsColors } from "/App.GeneratedEnums";
import Tags from "/Components/Blocks/Tags/Tags";
import { DesignTheme } from "/Models/Generated/DesignTheme";
import { InsightContext } from "/Models/Generated/InsightContext";
import { TeaserItemProps } from "/Models/Generated/TeaserItemProps";
import { createSpreadableBem } from "../../../Helpers/createBem";

export interface Props extends TeaserItemProps {
  wide?: boolean;
  medium?: boolean;
  propertyName?: string;
  useH2?: boolean;
  insightContext?: InsightContext;
  theme: DesignTheme;
  sidebar?: boolean;
  isFirst?: boolean;
}

const TeaserItem = ({
  propertyName,
  href,
  wide,
  medium,
  image,
  labels,
  heading,
  introText,
  useH2,
  theme,
  categoryTags,
  insightContext,
  sidebar,
  isFirst,
}: Props) => {
  const picture = (
    <Picture
      {...image}
      altText=""
      pictureAspectRatio="20/13"
      pictureBehaviour="Cover"
    />
  );
  const bem = createSpreadableBem("teaser-item-norad");
  const hideLabelAndIntro =
    insightContext === InsightContext.Level1 ||
    insightContext === InsightContext.Level2 ||
    insightContext === InsightContext.Article;

  const headingModifier =
    insightContext === InsightContext.Level1
      ? "--level1"
      : insightContext === InsightContext.Level2
        ? "--level2"
        : insightContext === InsightContext.Article
          ? "--article"
          : "";

  const tagsColor =
    insightContext === InsightContext.Level1 ||
    insightContext === InsightContext.Level2
      ? TagsColors.Insight
      : TagsColors.Standard;

  const text = (
    <div
      {...bem("__text-container", {
        "--wide": wide && !sidebar,
      })}
    >
      <div>
        {!hideLabelAndIntro && labels && (
          <div {...bem("__labels")}>
            <Labels {...labels} />
          </div>
        )}
        {heading && useH2 && (
          <h2
            {...bem("__header", {
              "--wide": wide && !sidebar,
              "--medium": medium,
              [headingModifier]: headingModifier !== "",
            })}
          >
            <span>{heading}</span>
          </h2>
        )}
        {heading && !useH2 && (
          <h3
            {...bem("__header", {
              "--wide": wide && !sidebar,
              "--medium": medium,
              [headingModifier]: headingModifier !== "",
            })}
          >
            <span>{heading}</span>
          </h3>
        )}
        {!hideLabelAndIntro && introText && (
          <p
            {...bem("__text", {
              "--wide": wide && !sidebar,
            })}
          >
            {introText}
          </p>
        )}
        {theme === DesignTheme.Insight && categoryTags && (
          <div
            {...bem("__tags-container", {
              "--wide": wide && !sidebar,
              "--sidebar": sidebar,
            })}
          >
            <Tags {...categoryTags} color={tagsColor} />
          </div>
        )}
      </div>
    </div>
  );

  return (
    <OptimizelyEditProperty
      clientRender={!!propertyName}
      propertyName={propertyName}
    >
      <article
        {...bem("", {
          [`--${insightContext?.toLowerCase()}`]: !!insightContext,
          "--first": isFirst,
        })}
      >
        <a
          href={href}
          {...bem("__link", {
            [`--${insightContext?.toLowerCase()}`]: !!insightContext,
          })}
        >
          {wide && !sidebar ? (
            <>
              <HideFor mobile tablet>
                <div {...bem("__focus-container")}>
                  <GridRow>
                    <GridPresetOneHalf overflowHidden>
                      {picture}
                    </GridPresetOneHalf>
                    <GridPresetOneHalf overflowHidden>{text}</GridPresetOneHalf>
                  </GridRow>
                </div>
              </HideFor>

              <HideFor desktop desktopMedium desktopLargeUp>
                <div {...bem("__container")}>
                  {image && (
                    <div {...bem("__picture-container")}>{picture}</div>
                  )}
                  {text}
                </div>
              </HideFor>
            </>
          ) : (
            <div {...bem("__container")}>
              {image && <div {...bem("__picture-container")}>{picture}</div>}
              {text}
            </div>
          )}
        </a>
      </article>
    </OptimizelyEditProperty>
  );
};

export default TeaserItem;
